import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAccount } from '@/hooks/useAccount'
import { useDream } from '@/hooks/useDream'
import { useLanguage } from '@/hooks/useLanguage'
import { useLoading } from '@/store/contexts/LoadingContext'
import { useToastContext } from '@/store/contexts/ToastContext'
import { t } from '@/utils/i18n'
import { formatCurrency } from '@/utils/currency'
import { DreamityAccountResDto, DreamityAccountSaveReqDto, DreamityAccountModifyReqDto, DreamityDepositReqDto, DreamityWithdrawReqDto } from '@/store/types/account.types'
import Card from '@/components/common/Card/Card'
import Button from '@/components/common/Button/Button'
import Modal from '@/components/common/Modal/Modal'
import { Input } from '@/components/common/Input/Input'
import Skeleton from '@/components/common/Skeleton/Skeleton'
import EmptyState from '@/components/common/EmptyState/EmptyState'
import PageHeader from '@/components/common/PageHeader/PageHeader'
// CreateButton removed per UI change (account creation button hidden)
import AccountActionButtons from '@/components/planner/AccountActionButtons/AccountActionButtons'

import { IconMoney, IconDream, IconClipboard } from '@/components/icons/Icons'

function Accounts() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dreamIdParam = searchParams.get('dreamId')
  const selectedDreamId = dreamIdParam || null
  
  const { language } = useLanguage()
  const { showToast } = useToastContext()
  const { startLoading, stopLoading } = useLoading()
  const { accounts, fetchAccounts, createAccount, updateAccount, deposit, withdraw, fetchTransactions, transactions, loading } = useAccount()
  const { dreams, fetchDreams, fetchDreamById, selectedDream } = useDream()

  useEffect(() => {
    if (!loading) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => stopLoading())
      })
    }
  }, [loading, stopLoading])

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false)
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false)
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false)
  const [isCreatingAccount, setIsCreatingAccount] = useState(false)
  const [isUpdatingAccount, setIsUpdatingAccount] = useState(false)
  const [isDepositing, setIsDepositing] = useState(false)
  const [isWithdrawing, setIsWithdrawing] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState<DreamityAccountResDto | null>(null)

  const [createForm, setCreateForm] = useState<DreamityAccountSaveReqDto>({
    dreamId: '',
    accountName: '',
    currentBalance: 0,
    targetAmount: 0,
  })

  const [editForm, setEditForm] = useState<DreamityAccountModifyReqDto>({
    accountName: '',
    currentBalance: 0,
    targetAmount: 0,
  })

  const [depositForm, setDepositForm] = useState<DreamityDepositReqDto>({
    amount: 0,
  })

  const [withdrawForm, setWithdrawForm] = useState<DreamityWithdrawReqDto>({
    amount: 0,
  })

 

  const lastFetchedDreamIdRef = useRef<string | null>(null)

  useEffect(() => {
    let mounted = true

    const load = async () => {
      if (!mounted) return
      if (selectedDreamId) {
        // prevent duplicate fetches for same dreamId (StrictMode double-run protection)
        if (lastFetchedDreamIdRef.current === selectedDreamId) return
        lastFetchedDreamIdRef.current = selectedDreamId

        try {
          await fetchAccounts(selectedDreamId)
          // Only fetch dream detail if we don't already have it
          if (!selectedDream || selectedDream.dreamId !== selectedDreamId) {
            await fetchDreamById(selectedDreamId)
          }
        } catch (err) {
          console.warn('Accounts page load failed', err)
        }
      } else {
        // no specific dream selected: fetch dreams list
        await fetchDreams()
      }
    }

    load()

    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDreamId])

  const handleCreateAccount = async () => {
    if (!createForm.accountName.trim()) {
      showToast(t('accounts.accountNameRequired', language) || '계좌 이름을 입력해주세요.', 'error')
      return
    }

    const dreamIdToUse = (selectedDreamId || createForm.dreamId || '').trim()
    if (!dreamIdToUse) {
      showToast(t('accounts.dreamRequired', language) || '꿈을 선택해주세요.', 'error')
      return
    }

    if (createForm.currentBalance < 0) {
      showToast(t('accounts.invalidBalance', language) || '잔액은 0 이상이어야 합니다.', 'error')
      return
    }

    if (createForm.targetAmount <= 0) {
      showToast(t('accounts.invalidTargetAmount', language) || '목표 금액은 0보다 커야 합니다.', 'error')
      return
    }

    try {
      setIsCreatingAccount(true)
      startLoading()
      await createAccount(dreamIdToUse, {
        dreamId: dreamIdToUse,
        accountName: createForm.accountName.trim(),
        currentBalance: createForm.currentBalance,
        targetAmount: createForm.targetAmount,
      })
      setIsCreateModalOpen(false)
      setCreateForm({
        dreamId: selectedDreamId || '',
        accountName: '',
        currentBalance: 0,
        targetAmount: 0,
      })
      showToast(t('messages.createSuccess', language), 'success')
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || t('messages.createFailed', language)
      showToast(msg, 'error')
    } finally {
      setIsCreatingAccount(false)
      stopLoading()
    }
  }

  

  const handleUpdateAccount = async () => {
    if (!selectedAccount) return

    if (!editForm.accountName.trim()) {
      showToast(t('accounts.accountNameRequired', language) || '계좌 이름을 입력해주세요.', 'error')
      return
    }

    if (editForm.currentBalance < 0) {
      showToast(t('accounts.invalidBalance', language) || '잔액은 0 이상이어야 합니다.', 'error')
      return
    }

    if (editForm.targetAmount <= 0) {
      showToast(t('accounts.invalidTargetAmount', language) || '목표 금액은 0보다 커야 합니다.', 'error')
      return
    }

    try {
      setIsUpdatingAccount(true)
      startLoading()
      await updateAccount(
        selectedAccount.dreamId || selectedDreamId || '',
        selectedAccount.accountId,
        editForm
      )
      setIsEditModalOpen(false)
      setSelectedAccount(null)
      showToast(t('messages.updateSuccess', language), 'success')
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || t('messages.updateFailed', language)
      showToast(msg, 'error')
    } finally {
      setIsUpdatingAccount(false)
      stopLoading()
    }
  }

  // Account deletion removed from UI — handler intentionally omitted

  const handleOpenDeposit = (account: DreamityAccountResDto) => {
    setSelectedAccount(account)
    setDepositForm({ amount: 0 })
    setIsDepositModalOpen(true)
  }

  const handleDeposit = async () => {
    if (!selectedAccount) return

    if (depositForm.amount <= 0) {
      showToast(t('accounts.invalidAmount', language) || '금액은 0보다 커야 합니다.', 'error')
      return
    }

    const dreamIdForRefresh = selectedAccount.dreamId ?? selectedDreamId ?? ''
    try {
      setIsDepositing(true)
      startLoading()
      await deposit(dreamIdForRefresh, selectedAccount.accountId, depositForm)
      if (dreamIdForRefresh) await fetchAccounts(dreamIdForRefresh)
      setIsDepositModalOpen(false)
      setDepositForm({ amount: 0 })
      setSelectedAccount(null)
      showToast(t('accounts.depositSuccess', language) || '입금이 완료되었습니다.', 'success')
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || (t('accounts.depositFailed', language) || '입금에 실패했습니다.')
      showToast(msg, 'error')
    } finally {
      setIsDepositing(false)
      stopLoading()
    }
  }

  const handleOpenWithdraw = (account: DreamityAccountResDto) => {
    setSelectedAccount(account)
    setWithdrawForm({ amount: 0 })
    setIsWithdrawModalOpen(true)
  }

  const handleWithdraw = async () => {
    if (!selectedAccount) return

    if (withdrawForm.amount <= 0) {
      showToast(t('accounts.invalidAmount', language) || '금액은 0보다 커야 합니다.', 'error')
      return
    }

    if (withdrawForm.amount > selectedAccount.currentBalance) {
      showToast(t('accounts.insufficientBalance', language) || '잔액이 부족합니다.', 'error')
      return
    }

    const dreamIdForRefresh = selectedAccount.dreamId ?? selectedDreamId ?? ''
    try {
      setIsWithdrawing(true)
      startLoading()
      await withdraw(dreamIdForRefresh, selectedAccount.accountId, withdrawForm)
      if (dreamIdForRefresh) await fetchAccounts(dreamIdForRefresh)
      setIsWithdrawModalOpen(false)
      setWithdrawForm({ amount: 0 })
      setSelectedAccount(null)
      showToast(t('accounts.withdrawSuccess', language) || '출금이 완료되었습니다.', 'success')
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || (t('accounts.withdrawFailed', language) || '출금에 실패했습니다.')
      showToast(msg, 'error')
    } finally {
      setIsWithdrawing(false)
      stopLoading()
    }
  }

  const handleViewTransactions = async (account: DreamityAccountResDto) => {
    setSelectedAccount(account)
    try {
      await fetchTransactions(account.dreamId || selectedDreamId || '', account.accountId)
      setIsTransactionModalOpen(true)
    } catch (error) {
      const msg = (error as any)?.response?.data?.message || (error as Error).message || (t('accounts.transactionFetchFailed', language) || '거래 내역 조회에 실패했습니다.')
      showToast(msg, 'error')
    }
  }

  if (loading && accounts.length === 0) {
    return (
      <div className="w-full max-w-full mx-auto p-0">
        <div className="flex justify-between items-center gap-4" style={{ marginBottom: 'var(--spacing-lg)' }}>
          <Skeleton height={32} width={150} />
          <Skeleton height={40} width={120} />
        </div>
        <div className="grid grid-cols-1 min-[480px]:grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={{ minHeight: 200, width: '100%' }}>
              <Skeleton height={200} variant="rectangular" style={{ width: '100%' }} />
            </div>
          ))}
        </div>
      </div>
    )
  }

  // 선택된 꿈이 있으면 해당 꿈의 계좌만 필터링
  const filteredAccounts = selectedDreamId 
    ? accounts.filter(account => account.dreamId === selectedDreamId)
    : accounts

  return (
    <div className="w-full max-w-full mx-auto p-0">
      <PageHeader showBack={false} rightContent={null} onBack={() => navigate(-1)} />

      <div className="grid grid-cols-1 min-[480px]:grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-6 animate-[fadeIn_0.3s_ease-out]">
        {filteredAccounts.length === 0 ? (
          <EmptyState
            icon={<IconMoney size={32} />}
            title={t('accounts.noAccounts', language) || '계좌가 없습니다.'}
            description={t('accounts.noAccountsDesc', language) || '관리자는 해당 꿈에 계좌를 추가할 수 있습니다.'}
          />
        ) : (
          filteredAccounts.map((account) => {
            const progress = account.targetAmount > 0 
              ? Math.min((account.currentBalance / account.targetAmount) * 100, 100) 
              : 0

            return (
              <Card
                key={account.accountId}
                className="transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] border-l-4 border-l-gray-400 hover:-translate-y-1 hover:shadow-[var(--shadow-lg)] hover:border-l-gray-500"
              >
                <div className="flex justify-between items-start mb-4 pb-4 border-b-2 border-[var(--color-card-border)]">
                  <div className="flex-1 min-w-0">
                    <h3 className="m-0 mb-1 text-lg font-semibold text-[var(--color-text-primary)]">{account.accountName}</h3>
                    <p className="m-0 text-sm text-[var(--color-text-secondary)] flex items-center gap-1">
                      <IconDream size={16} className="shrink-0" /> {account.dreamName}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-md text-base leading-none bg-transparent cursor-pointer transition-all duration-200 hover:bg-[var(--color-background)] hover:scale-110 focus:outline-none"
                      onClick={() => handleViewTransactions(account)}
                      title={t('accounts.transactions', language) || '거래 내역'}
                    >
                      <IconClipboard size={18} />
                    </button>
                  </div>
                </div>
                <div className="flex flex-col gap-4 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-[var(--color-text-secondary)]">{t('accounts.currentBalance', language) || '현재 잔액'}</span>
                    <span className="text-lg font-semibold text-[#424242]">{formatCurrency(account.currentBalance, language)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-[var(--color-text-secondary)]">{t('accounts.targetAmount', language) || '목표 금액'}</span>
                    <span className="text-lg font-semibold text-[#424242]">{formatCurrency(account.targetAmount, language)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-3 bg-[var(--color-background)] rounded-xl overflow-hidden shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]">
                      <div
                        className="h-full rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 transition-[width] duration-500 ease-out shadow-[0_2px_4px_rgba(117,117,117,0.2)]"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-[var(--color-text-primary)] min-w-[45px] text-right">{Math.round(progress)}%</span>
                  </div>
                </div>
                <AccountActionButtons
                  account={account}
                  onDeposit={handleOpenDeposit}
                  onWithdraw={handleOpenWithdraw}
                />
              </Card>
            )
          })
        )}
      </div>

      {/* 계좌 생성 모달 */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          if (!isCreatingAccount) {
            setIsCreateModalOpen(false)
            setCreateForm({
              dreamId: selectedDreamId || '',
              accountName: '',
              currentBalance: 0,
              targetAmount: 0,
            })
          }
        }}
        title={t('accounts.createTitle', language) || '계좌 생성'}
        size="md"
      >
        <div className={`flex flex-col gap-4 ${isCreatingAccount ? 'pointer-events-none opacity-50' : ''}`}>
          {!selectedDreamId && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-[var(--color-text-primary)]">
                {t('accounts.dreamSelect', language) || '꿈 선택'}
                <span className="text-[var(--color-error)]">*</span>
              </label>
              <select
                value={createForm.dreamId || ''}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    dreamId: e.target.value,
                  })
                }
                className="w-full px-4 py-3 border-2 border-[var(--color-card-border)] rounded-xl bg-[var(--color-card)] text-[var(--color-text-primary)] text-base cursor-pointer transition-all duration-300 appearance-none bg-no-repeat bg-[length:12px_12px] bg-[right_1rem_center] pr-12 focus:outline-none focus:border-[var(--color-primary)] focus:ring-4 focus:ring-[var(--color-primary)]/15"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                }}
                required
              >
                <option value="">{t('accounts.selectDream', language) || '꿈을 선택하세요'}</option>
                {dreams.map((dream) => (
                  <option key={dream.dreamId} value={dream.dreamId}>
                    {dream.dreamName}
                  </option>
                ))}
              </select>
            </div>
          )}
          {selectedDreamId && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-[var(--color-text-primary)]">{t('dreams.dreamName', language) || '꿈 이름'}</label>
              <Input
                value={selectedDream?.dreamName || ''}
                readOnly
                className="opacity-70 cursor-not-allowed bg-[var(--color-background)]"
              />
            </div>
          )}
          <Input
            label={t('accounts.accountName', language) || '계좌 이름'}
            value={createForm.accountName}
            onChange={(e) =>
              setCreateForm({ ...createForm, accountName: e.target.value })
            }
            placeholder={t('accounts.accountNamePlaceholder', language) || '계좌 이름을 입력하세요'}
            required
          />
          <Input
            label={t('accounts.currentBalance', language) || '현재 잔액'}
            type="number"
            value={createForm.currentBalance || ''}
            min={0}
            onChange={(e) =>
              setCreateForm({
                ...createForm,
                currentBalance: Math.max(0, parseFloat(e.target.value) || 0),
              })
            }
          />
          <Input
            label={t('accounts.targetAmount', language) || '목표 금액'}
            type="number"
            value={createForm.targetAmount || ''}
            min={0}
            onChange={(e) =>
              setCreateForm({
                ...createForm,
                targetAmount: Math.max(0, parseFloat(e.target.value) || 0),
              })
            }
          />
          <div className="flex justify-end gap-4 mt-6 pt-4 border-t border-[var(--color-card-border)]">
            <Button variant="primary" onClick={handleCreateAccount} disabled={isCreatingAccount}>
              {isCreatingAccount ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-[var(--color-primary)]/30 border-t-[var(--color-primary)] rounded-full animate-spin mr-2 align-middle" />
                  {t('messages.processing', language) || '처리중입니다...'}
                </>
              ) : (
                t('common.create', language)
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* 계좌 수정 모달 */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          if (!isUpdatingAccount) {
            setIsEditModalOpen(false)
            setSelectedAccount(null)
          }
        }}
        title={t('accounts.editTitle', language) || '계좌 수정'}
        size="md"
      >
        <div className={`flex flex-col gap-4 ${isUpdatingAccount ? 'pointer-events-none opacity-50' : ''}`}>
          <Input
            label={t('accounts.accountName', language) || '계좌 이름'}
            value={editForm.accountName}
            onChange={(e) =>
              setEditForm({ ...editForm, accountName: e.target.value })
            }
            required
          />
          <Input
            label={t('accounts.currentBalance', language) || '현재 잔액'}
            type="number"
            value={editForm.currentBalance || ''}
            min={0}
            onChange={(e) =>
              setEditForm({
                ...editForm,
                currentBalance: Math.max(0, parseFloat(e.target.value) || 0),
              })
            }
          />
          <Input
            label={t('accounts.targetAmount', language) || '목표 금액'}
            type="number"
            value={editForm.targetAmount || ''}
            min={0}
            onChange={(e) =>
              setEditForm({
                ...editForm,
                targetAmount: Math.max(0, parseFloat(e.target.value) || 0),
              })
            }
          />
          <div className="flex justify-end gap-4 mt-6 pt-4 border-t border-[var(--color-card-border)]">
            <Button variant="primary" onClick={handleUpdateAccount} disabled={isUpdatingAccount}>
              {isUpdatingAccount ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-[var(--color-primary)]/30 border-t-[var(--color-primary)] rounded-full animate-spin mr-2 align-middle" />
                  {t('messages.processing', language) || '처리중입니다...'}
                </>
              ) : (
                t('common.save', language)
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* 입금 모달 */}
      <Modal
        isOpen={isDepositModalOpen}
        onClose={() => {
          if (!isDepositing) {
            setIsDepositModalOpen(false)
            setDepositForm({ amount: 0 })
            setSelectedAccount(null)
          }
        }}
        title={t('accounts.depositTitle', language) || '입금'}
        size="sm"
      >
        <div className={`relative flex flex-col gap-4 ${isDepositing ? 'pointer-events-none opacity-50' : ''}`}>
          {selectedAccount && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-[var(--color-text-primary)]">{t('accounts.accountName', language) || '계좌 이름'}</label>
                <Input
                  value={selectedAccount.accountName}
                  readOnly
                  className="opacity-70 cursor-not-allowed bg-[var(--color-background)]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-[var(--color-text-primary)]">{t('accounts.currentBalance', language) || '현재 잔액'}</label>
                <Input
                  value={formatCurrency(selectedAccount.currentBalance, language)}
                  readOnly
                  className="opacity-70 cursor-not-allowed bg-[var(--color-background)]"
                />
              </div>
              <Input
                label={t('accounts.amount', language) || '금액'}
                type="number"
                value={depositForm.amount || ''}
                min={0}
                onChange={(e) =>
                  setDepositForm({
                    amount: Math.max(0, parseFloat(e.target.value) || 0),
                  })
                }
                placeholder={t('accounts.amountPlaceholder', language) || '입금할 금액을 입력하세요'}
              />
              <div className="flex justify-end gap-4 mt-6 pt-4 border-t border-[var(--color-card-border)]">
                <Button variant="primary" onClick={handleDeposit} disabled={isDepositing}>
                  {isDepositing ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-[var(--color-primary)]/30 border-t-[var(--color-primary)] rounded-full animate-spin mr-2 align-middle" />
                      {t('messages.processing', language) || '처리중입니다...'}
                    </>
                  ) : (
                    t('accounts.deposit', language) || '입금'
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* 출금 모달 */}
      <Modal
        isOpen={isWithdrawModalOpen}
        onClose={() => {
          if (!isWithdrawing) {
            setIsWithdrawModalOpen(false)
            setWithdrawForm({ amount: 0 })
            setSelectedAccount(null)
          }
        }}
        title={t('accounts.withdrawTitle', language) || '출금'}
        size="sm"
      >
        <div className={`relative flex flex-col gap-4 ${isWithdrawing ? 'pointer-events-none opacity-50' : ''}`}>
          {selectedAccount && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-[var(--color-text-primary)]">{t('accounts.accountName', language) || '계좌 이름'}</label>
                <Input
                  value={selectedAccount.accountName}
                  readOnly
                  className="opacity-70 cursor-not-allowed bg-[var(--color-background)]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-[var(--color-text-primary)]">{t('accounts.currentBalance', language) || '현재 잔액'}</label>
                <Input
                  value={formatCurrency(selectedAccount.currentBalance, language)}
                  readOnly
                  className="opacity-70 cursor-not-allowed bg-[var(--color-background)]"
                />
              </div>
              <Input
                label={t('accounts.amount', language) || '금액'}
                type="number"
                value={withdrawForm.amount || ''}
                min={0}
                onChange={(e) =>
                  setWithdrawForm({
                    amount: Math.max(0, parseFloat(e.target.value) || 0),
                  })
                }
                placeholder={t('accounts.amountPlaceholder', language) || '출금할 금액을 입력하세요'}
              />
              <div className="flex justify-end gap-4 mt-6 pt-4 border-t border-[var(--color-card-border)]">
                <Button variant="primary" onClick={handleWithdraw} disabled={isWithdrawing}>
                  {isWithdrawing ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-[var(--color-primary)]/30 border-t-[var(--color-primary)] rounded-full animate-spin mr-2 align-middle" />
                      {t('messages.processing', language) || '처리중입니다...'}
                    </>
                  ) : (
                    t('accounts.withdraw', language) || '출금'
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* 잔액 조정 기능 삭제됨 */}

      {/* 거래 내역 모달 */}
      <Modal
        isOpen={isTransactionModalOpen}
        onClose={() => {
          setIsTransactionModalOpen(false)
          setSelectedAccount(null)
        }}
        title={t('accounts.transactionsTitle', language) || '거래 내역'}
        size="lg"
      >
        <div className="flex flex-col gap-4">
          {selectedAccount && (
            <>
              <div className="mb-6 pb-4 border-b-2 border-[var(--color-card-border)]">
                <h4 className="m-0 mb-1 text-lg font-semibold text-[var(--color-text-primary)]">{selectedAccount.accountName}</h4>
                <p className="m-0 text-sm text-[var(--color-text-secondary)]">{t('accounts.currentBalance', language) || '현재 잔액'}: {formatCurrency(selectedAccount.currentBalance, language)}</p>
              </div>
              <div className="flex flex-col gap-2 max-h-[500px] overflow-y-auto">
                {transactions.length === 0 ? (
                  <EmptyState
                    icon={<IconClipboard size={32} />}
                    title={t('accounts.noTransactions', language) || '거래 내역이 없습니다.'}
                    description={t('accounts.noTransactionsDesc', language) || '아직 거래 내역이 없습니다.'}
                  />
                ) : (
                  transactions.map((transaction) => (
                    <div
                      key={transaction.transactionId}
                      className="flex justify-between items-start p-4 bg-[var(--color-background)] rounded-lg border-l-[3px] border-l-[var(--color-card-border)] transition-all duration-200 hover:bg-[var(--color-card)] hover:border-l-gray-400 hover:shadow-[var(--shadow-sm)]"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-[var(--color-text-secondary)] mb-1">
                          {new Date(transaction.transactionDate).toLocaleString(language === 'ko' ? 'ko-KR' : 'en-US')}
                        </div>
                        <div className="text-sm font-medium text-[var(--color-text-primary)]">
                          {transaction.description || (transaction.amount > 0 ? t('accounts.deposit', language) || '입금' : t('accounts.withdraw', language) || '출금')}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`text-base font-semibold ${transaction.amount > 0 ? 'text-gray-600' : 'text-gray-500'}`}>
                          {transaction.amount > 0 ? '+' : ''}{formatCurrency(transaction.amount, language)}
                        </span>
                        <div className="text-xs text-[var(--color-text-secondary)]">
                          잔액: {formatCurrency(transaction.balance, language)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default Accounts
